'use client'

/**
 * StockHubTabs — Yahoo-style tab bar for the stock hub (/stocks/[symbol]).
 *
 * SEO-critical: every tab's content is passed in already server-rendered and ALL panels stay in the
 * DOM — inactive ones are just visually hidden (not unmounted) — so crawlers see every tab's content.
 * The client only toggles which panel is visible. Server components (news, activity) and client
 * components (feed, tape) both work as `node` because the server page renders them and passes them in.
 *
 * ⚡ `lazy` (2026-10-23, register mobile-first-paint-1021, owner chose "load only the extras"). A tab may
 * also pass `lazy`: the full live component, mounted the FIRST time the tab is opened and kept after.
 * Until then the panel shows `node`, which for those tabs carries only the items the visible Overview
 * does not already show, so a crawler still reads every item once. It is why the Alerts and Community
 * tabs no longer ship ~850 duplicate elements on every stock page, and why their live tape / feed no
 * longer poll the server every 12-15s for a tab nobody opened.
 */

import { useState, type ReactNode } from 'react'

export type HubTab = { id: string; label: string; badge?: number | string; node: ReactNode; lazy?: ReactNode }

export default function StockHubTabs({ tabs }: { tabs: HubTab[] }) {
  const [active, setActive] = useState(tabs[0]?.id ?? '')
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set([tabs[0]?.id ?? '']))
  const open = (id: string) => {
    setActive(id)
    setOpened((prev) => (prev.has(id) ? prev : new Set(prev).add(id)))
  }

  return (
    <div>
      <div role="tablist" aria-label="Stock sections" className="mb-5 flex flex-wrap gap-0.5 border-b border-border">
        {tabs.map((t) => {
          const on = active === t.id
          return (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => open(t.id)}
              className={[
                'relative -mb-px inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold transition-colors sm:px-4',
                on ? 'border-b-2 border-amber-400 text-amber-600 dark:text-amber-300' : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {t.label}
              {t.badge != null && t.badge !== 0 && t.badge !== '' && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${on ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300' : 'bg-muted text-muted-foreground'}`}>{t.badge}</span>
              )}
            </button>
          )
        })}
      </div>

      {tabs.map((t) => (
        <div key={t.id} role="tabpanel" aria-hidden={active !== t.id} hidden={active !== t.id} className={active === t.id ? '' : 'hidden'}>
          {t.lazy && opened.has(t.id) ? t.lazy : t.node}
        </div>
      ))}
    </div>
  )
}
